import {connect, useSelector} from "react-redux";
import React, {useEffect, useState} from "react";
import Loading from "../../common/Loading";
import * as api from "../../../api/api";
import {AgGridReact} from "ag-grid-react";
import * as Global from "../../common/Global";
import dayjs from "dayjs";
import InputRangeDate from "../../common/InputRangeDate";
import {useSearchParams} from "react-router-dom";
import {StandardListPage} from "../../../components/StandardListPage";

const vendorName = (vendorCode) => {
    switch (vendorCode) {
        case "pragmatic_slot": return "프라그마틱 슬롯";
        case "pragmatic_casino": return "프라그마틱 카지노";
        default: return vendorCode || '';
    }
};

const CasinoBettingList = ({ paramUserId = '', embedded = false }) => {
    const {user} = useSelector((props) => props.account);
    const [searchParams] = useSearchParams();
    const initialUserId = paramUserId || searchParams.get('userId') || '';

    const [loading, setLoading] = useState(false)
    const [searchForm, setSearchForm] = useState({
        fromDate: dayjs(),
        toDate: dayjs(),
        userId: initialUserId,
        includeSubUsers: false
    })
    const [dataList, setDataList] = useState([])
    const [sumData, setSumData] = useState({
        totalBettingAmount: 0, totalWinningAmount: 0, totalFee: 0, totalProfit: 0
    })
    const [page, setPage] = useState(0)
    const [size, setSize] = useState(100)
    const [meta, setMeta] = useState({
        page: 0,
        size: 100,
        totalElements: 0,
        totalPages: 0,
        hasNext: false
    })

    const defaultColDef = {
        resizable: true,
        sortable: true,
        filter: false,
        autoHeaderHeight: true,
        suppressMenu: true,
        suppressHeaderMenuButton: true
    };

    const [columnDefs] = useState([
        {field: 'No', headerName: '번호', width: 60, valueGetter: 'node.rowIndex + 1', cellClass: 'static-cell'},
        {field: 'seq', headerName: 'No', width: 40, hide: true},
        {field: 'bettingDt', headerName: '베팅일자', width: 100,
            valueFormatter: (params) => params.value ? dayjs(params.value, 'YYYYMMDD').format('YYYY-MM-DD') : ''
        },
        {
            field: 'bettingUserId',
            headerName: "아이디",
            width: 180,
            cellStyle: { cursor: 'pointer', color: '#3B82F6', fontWeight: 'bold' },
            valueGetter: (params) => params.data ? `${params.data.bettingUserId} (${params.data.nickName || ''})` : ''
        },
        {field: 'vendorCode', headerName: "종목", width: 180, valueGetter: (params) => vendorName(params.data?.vendorCode), cellClass: 'static-cell'},
        {field: 'gameName', headerName: '게임', width: 200},
        {
            field: 'createdAt',
            headerName: "배팅시각",
            width: 180,
            cellClass: 'static-cell',
            valueFormatter: (params) => params.value ? dayjs(params.value).format('YYYY-MM-DD HH:mm:ss') : ''
        },
        {field: 'userBalance', headerName: "베팅전 금액", width: 100,
            valueGetter: params => params.data.vendorCode === 'POSEIDON' ? params.data.userBalance : params.data.userBalance + params.data.amount,
            valueFormatter: Global.currencyFormatter, cellClass: 'number-cell'},
        {field: 'amount', headerName: "배팅금", width: 100, valueFormatter: Global.currencyFormatter, cellClass: 'number-cell'},
        {headerName: "베팅후 금액", width: 100,
            valueGetter: params => params.data.vendorCode === 'POSEIDON' ? params.data.userBalance - params.data.amount + params.data.winAmount : params.data.userBalance,
            valueFormatter: Global.currencyFormatter,
            cellClass: 'text-green-600 number-cell'
        },
        {field: 'winAmount', headerName: "당첨금", width: 100, valueFormatter: Global.currencyFormatter, cellClass: 'number-cell'},
        {
            field: 'status',
            headerName: "승패",
            width: 100,
            cellClass: 'static-cell',
            valueGetter: e => {
                if (e.data.status === 0) return '대기'
                if (e.data.status === 1) return '적중'
                if (e.data.status === 2) return '실격'
                if (e.data.status === 80) return '적특'
                if (e.data.status === 99) return '취소'
                return ''
            },
            cellStyle: params => {
                if (params.data.status === 1) return { color: 'green' }
                if (params.data.status === 2) return { color: 'red' }
                return null
            }
        },
        {field: 'transactionCode', headerName: "검증코드", width: 300, cellStyle: { cursor: 'pointer', color: '#3B82F6', fontWeight: 'bold' }}
    ])

    useEffect(() => {
        search(0)
    }, [])

    const handleWhere = (e) => {
        const {name, value} = e.target;
        const isCheckbox = e.target.type === 'checkbox';
        setSearchForm({
            ...searchForm,
            [name]: isCheckbox ? e.target.checked : value
        })
    }

    const search = (pageNum = 0, pageSize = null) => {
        if (searchForm.includeSubUsers && (!searchForm.userId || searchForm.userId.trim() === '')) {
            window.alert('하부포함을 선택한 경우 회원ID는 필수 입력 항목입니다.')
            return;
        }
        const currentSize = pageSize ?? size
        if (pageSize != null) setSize(currentSize)
        const params = {
            companyCode: user.companyCode,
            rootUserId: user.rootUserId,
            fromDate: searchForm.fromDate.format("YYYYMMDD"),
            toDate: searchForm.toDate.format("YYYYMMDD"),
            vendorTypeCode: "casino",
            userId: searchForm.userId,
            includeSubUsers: searchForm.includeSubUsers,
            page: pageNum,
            size: currentSize
        }
        setLoading(true)
        api.getCasinoBettingList(params).then(result => {
            const {data, status} = result;
            if (status === 200) {
                const list = data?.bettingList ?? []
                const summary = data?.summary ?? {}
                const metaData = data?.meta ?? {}
                setDataList(list)
                setPage(pageNum)
                setMeta({
                    page: metaData.page ?? pageNum,
                    size: metaData.size ?? currentSize,
                    totalElements: metaData.totalElements ?? 0,
                    totalPages: metaData.totalPages ?? 0,
                    hasNext: metaData.hasNext ?? false
                })
                setSumData({
                    totalBettingAmount: summary.totalBettingAmount ?? 0,
                    totalWinningAmount: summary.totalWinAmount ?? 0,
                    totalFee: summary.totalFee ?? 0,
                    totalProfit: summary.profit ?? 0
                })
            }
        })
            .catch(ex => window.alert(ex.message))
            .finally(() => setLoading(false))
    }

    const onCellClicked = (params) => {
        if (params.column.colId === 'bettingUserId') handleUserClick(params.data);
        if (params.column.colId === 'transactionCode') handleTransactionCodeClick(params.data);
    }

    const handleUserClick = (data) => {
        const q = "userId=" + data.bettingUserId + "&procUserId=" + user.userId
        const popup = window.open('/#/user/detail?' + q, "_user-detail", "width=1500, height=800");
        if (popup) popup.focus();
    }

    const handleTransactionCodeClick = (row) => {
        api.getMiracleGameResult({ companyCode: user.companyCode, transactionCode: row.transactionCode }).then(result => {
            if (result.status === 200 && result.data?.data) {
                const popup = window.open(result.data.data, "_blank");
                if (popup) popup.focus();
            }
        }).catch(ex => window.alert(ex.message));
    }

    const handleDate = (date) => {
        setSearchForm({ ...searchForm, fromDate: date[0], toDate: date[1] })
    }

    const filterContent = (
        <>
            <InputRangeDate title="날짜" startTime={searchForm.fromDate} endTime={searchForm.toDate} setValue={handleDate} />
            <input
                className="h-7 px-2 w-40 text-xs border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                name="userId"
                value={searchForm.userId}
                onChange={handleWhere}
                onKeyPress={e => e.key === "Enter" && search(0)}
                placeholder="회원ID"
            />
            <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                <input
                    type="checkbox"
                    name="includeSubUsers"
                    checked={searchForm.includeSubUsers}
                    onChange={handleWhere}
                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                하부포함
            </label>
            <button className="h-7 px-3 bg-gray-600 text-white text-xs font-medium" onClick={() => search(0)}>조회</button>
        </>
    );

    const summaryRow = (
        <div className="grid grid-cols-4 gap-0 py-2.5 px-3 bg-slate-500 border-b border-slate-600 text-sm">
            <div className="text-slate-100 font-medium text-center">총 배팅금액 <span className="block mt-0.5"><strong className="text-red-200 font-bold text-base">{sumData.totalBettingAmount.toLocaleString()}</strong></span></div>
            <div className="text-slate-100 font-medium text-center border-l border-slate-400">총 당첨금액 <span className="block mt-0.5"><strong className="text-blue-200 font-bold text-base">{sumData.totalWinningAmount.toLocaleString()}</strong></span></div>
            <div className="text-slate-100 font-medium text-center border-l border-slate-400">총 수수료 <span className="block mt-0.5"><strong className="text-amber-200 font-bold text-base">{sumData.totalFee.toLocaleString()}</strong></span></div>
            <div className="text-slate-100 font-medium text-center border-l border-slate-400">수익 <span className="block mt-0.5"><strong className="text-emerald-200 font-bold text-base">{sumData.totalProfit.toLocaleString()}</strong></span></div>
        </div>
    );

    const paginationBar = (
        <div className="flex flex-row items-center justify-between gap-4 py-2 px-0 border-t border-gray-100 text-xs">
            <div className="flex items-center gap-2">
                <span className="text-gray-600">총</span>
                <select
                    value={size}
                    onChange={(e) => search(0, Number(e.target.value))}
                    className="h-7 border border-gray-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                    {[100, 300, 500].map((n) => <option key={n} value={n}>{n}건</option>)}
                </select>
                <span className="text-gray-600">/ 전체 <strong>{meta.totalElements.toLocaleString()}</strong>건</span>
            </div>
            <div className="flex items-center gap-1">
                <button type="button" disabled={meta.page <= 0} onClick={() => search(0)} className="h-7 px-2 text-xs border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100">처음</button>
                <button type="button" disabled={meta.page <= 0} onClick={() => search(meta.page - 1)} className="h-7 px-2 text-xs border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100">이전</button>
                <span className="px-2 py-1 text-gray-700">
                    {meta.totalElements === 0 ? '0' : `${meta.page * meta.size + 1}-${Math.min((meta.page + 1) * meta.size, meta.totalElements)}`} / {meta.totalPages} 페이지
                </span>
                <button type="button" disabled={!meta.hasNext || meta.totalPages <= 0} onClick={() => search(meta.page + 1)} className="h-7 px-2 text-xs border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100">다음</button>
                <button type="button" disabled={!meta.hasNext || meta.totalPages <= 0} onClick={() => search(meta.totalPages - 1)} className="h-7 px-2 text-xs border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100">마지막</button>
            </div>
        </div>
    );

    const gridContent = (
        <>
            {summaryRow}
            <div className="ag-theme-alpine h-[calc(100vh-370px)] overflow-hidden mt-1">
                <AgGridReact
                    rowHeight={36}
                    headerHeight={32}
                    rowData={dataList}
                    pagination={false}
                    enableCellTextSelection={true}
                    enableRangeSelection={true}
                    suppressClipboardPaste={true}
                    defaultColDef={defaultColDef}
                    columnDefs={columnDefs}
                    onCellClicked={onCellClicked}
                    onGridReady={(params) => params.api.sizeColumnsToFit()}
                />
            </div>
            {paginationBar}
        </>
    );

    if (embedded) {
        return (
            <>
                {loading ? <Loading /> : null}
                <div className="bg-white border border-gray-200">
                    <div className="px-2 py-2 border-b border-gray-100 flex flex-wrap items-center gap-2">
                        {filterContent}
                    </div>
                    <div className="p-2">
                        {gridContent}
                    </div>
                </div>
            </>
        );
    }

    return (
        <StandardListPage title="카지노 배팅" loading={loading} filterContent={filterContent}>
            {gridContent}
        </StandardListPage>
    );
}

const mapStateToProps = (state) => ({ account: state.account, store: state.store });

export default connect(mapStateToProps, null)(CasinoBettingList);
